import { useCallback, useMemo, useState } from 'react';
import {
  addCalendarDays,
  addMonthsKeepingDayOne,
  firstDayOfMonth,
  getBusinessDate,
  isAfterCalendarDate,
  isBeforeCalendarDate,
  type JstCalendarDate,
} from '@/utils/businessDate';
import { toDateString } from '@/utils/datetime';

export const useDatePicker = ({
  maxDaysFromToday,
  onDateSelect,
}: {
  maxDaysFromToday: number;
  onDateSelect: (date: string) => void;
}) => {
  const today = useMemo(getBusinessDate, []);

  const [currentMonth, setCurrentMonth] = useState<JstCalendarDate>(today);

  // カレンダーで有効な月が選択されているかのフラグ
  // 次月に表示を切り替えたときに、次月が選択可能な期間外なのであれば、カレンダーより先のUIを非表示にする対応のため。
  const isValidMonth = useMemo(() => {
    const maxDate = addCalendarDays(today, maxDaysFromToday);
    const firstDayOfCurrentMonth = firstDayOfMonth(currentMonth);
    return !isAfterCalendarDate(firstDayOfCurrentMonth, maxDate);
  }, [currentMonth, maxDaysFromToday, today]);

  const changeMonth = useCallback(
    (direction: number) => {
      // 月の1日を基準に計算することで、月末日の繰り上げ問題を回避
      // (例: 1月31日 + 1ヶ月 → 3月3日になる問題)
      const newMonth = addMonthsKeepingDayOne(currentMonth, direction);
      setCurrentMonth(newMonth);

      // 次月に移動した場合、選択可能な最初の日付を自動選択する
      if (direction > 0) {
        const businessToday = getBusinessDate();
        const maxDate = addCalendarDays(businessToday, maxDaysFromToday);

        // 次月の1日
        const firstDayOfNewMonth = firstDayOfMonth(newMonth);

        // 次月の1日が選択可能範囲内かつ、営業日以降の場合、次月の1日を選択
        if (
          !isBeforeCalendarDate(firstDayOfNewMonth, businessToday) &&
          !isAfterCalendarDate(firstDayOfNewMonth, maxDate)
        ) {
          onDateSelect(toDateString(firstDayOfNewMonth));
        }
      }
    },
    [currentMonth, maxDaysFromToday, onDateSelect],
  );

  return {
    currentMonth,
    isValidMonth,
    changeMonth,
    setCurrentMonth,
  };
};
